import { Autocomplete, InputAdornment, Popover } from '@mui/material';
import styles from './conversation-create-group-popover.module.scss';
import {
  CcfAvailableIcon,
  CcfAvatar,
  CcfBox,
  CcfButton,
  CcfCloseIcon,
  CcfOfflineIcon,
  CcfSearchIcon,
  CcfTextField,
  CcfTypography,
  CcfUnavailableIcon,
  CcfWorkingIcon,
  useTranslator,
} from '@nice-ccf/ui-controls';
import { useState } from 'react';
import {
  ccfConversationsActions,
  getAgentGroups,
} from '../+state/ccf-conversations.slice';
import { useDispatch, useSelector } from 'react-redux';
import { activeAgentsList } from '../mock-data/agent-conversations-mock-data';

/* eslint-disable-next-line */
export interface ConversationCreateGroupPopoverProps {
  anchorEl?: any;
  handleClose: () => void;
}

/**
 * Function to be used for create group popover
 * @param param0 - React.ReactNode
 * @example <ConversationCreateGroupPopover />
 * @returns
 */
export function ConversationCreateGroupPopover(
  props: ConversationCreateGroupPopoverProps
) {
  const { anchorEl, handleClose } = props;
  const agentGroups = useSelector(getAgentGroups);
  const [translate] = useTranslator();
  const dispatch = useDispatch();
  const agentsList = activeAgentsList;
  const { updateSelectedAgent, updateAgentGroups } = ccfConversationsActions;
  const [groupName, setgroupName] = useState('');
  const [searchTxt, setSearchTxt] = useState('');
  const [filteredAgentList, setFilteredAgentList] = useState<any[]>([]);
  const [groupMembers, setGroupMembers] = useState<any[]>([]);
  const avatarColors = ['#13DFCA', '#FFC107', '#FF5722', '#9C27B0', '#3F51B5'];

  /**
   * Handler for search agents across agent directory
   * @example searchAgentHandler()
   */
  const searchAgentHandler = (e: any) => {
    const searchText = e.target.value.toLowerCase();
    setSearchTxt(searchText);
    if (searchText) {
      const searchedAgents = agentsList.filter((agent) =>
        agent.name.toLowerCase().startsWith(searchText)
      );
      setFilteredAgentList(searchedAgents);
    } else {
      setFilteredAgentList([]);
    }
  };

  /**
   * Handler for adding agent to the group
   * @example addAgentHandler()
   */
  const addAgentHandler = (agent: any) => {
    setGroupMembers((prevMembers) => {
      if (!prevMembers.some((member) => member.name === agent.name)) {
        return [...prevMembers, agent];
      }
      return prevMembers;
    });
    setSearchTxt('');
    setFilteredAgentList([]);
  };

  /**
   * Handler for removing the agent from the group
   * @example removeAgentHandler()
   */
  const removeMemberHandler = (agent: any) => {
    setGroupMembers((prevMembers) => {
      return prevMembers.filter((member) => member.name !== agent.name);
    });
  };

  /**
   * Handler for creating a new group
   * @example createGroupHandler()
   */
  const createGroupHandler = () => {
    if (!groupName.trim() || groupMembers.length === 0) {
      return;
    }
    const createdGroups = agentGroups;
    const matchingGroup = createdGroups.find(
      (grp: any) => grp.name.toLowerCase() === groupName.toLowerCase()
    );
    if (matchingGroup !== null && matchingGroup !== undefined) {
      const updateNewGroup = {
        name: matchingGroup.name.trim(),
        messages: [...matchingGroup.messages],
        groupMembers: [
          ...matchingGroup.groupMembers,
          ...groupMembers.filter(
            (newMember) =>
              !matchingGroup.groupMembers.some(
                (existingMember: any) => existingMember.name === newMember.name
              )
          ),
        ],
        isMessageRead: true,
      };
      dispatch(updateSelectedAgent({ agent: updateNewGroup, type: 'group' }));
      const action = updateAgentGroups;
      dispatch(action({ agent: updateNewGroup, type: 'update' }));
    } else {
      const newGroup = {
        name: groupName.trim(),
        messages: [],
        groupMembers: groupMembers,
        isMessageRead: true,
      };

      dispatch(updateAgentGroups({ newGroup, type: 'add' }));
      dispatch(updateSelectedAgent({ agent: newGroup, type: 'group' }));
    }
    handleClose();
  };

  /**
   * Function to render status icon
   * @param e - any
   * @example renderStatusIcon(status)
   */
  function renderStatusIcon(status: string) {
    switch (status) {
      case 'working':
        return <CcfWorkingIcon className={styles.statusIcon} />;
      case 'available':
        return <CcfAvailableIcon className={styles.statusIcon} />;
      case 'busy':
        return <CcfUnavailableIcon className={styles.statusIcon} />;
      case 'offline':
        return (
          <CcfOfflineIcon
            className={styles.statusIcon}
            style={{ color: 'inherit' }}
          />
        );
      default:
        return <CcfAvailableIcon className={styles.statusIcon} />;
    }
  }

  return (
    <Popover
      id={'create-group-popover'}
      open={Boolean(anchorEl)}
      keepMounted
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      style={{ marginLeft: '10px' }}
    >
      <CcfBox className={styles.createGroupPopoverContainer}>
        <CcfBox className={styles.groupBlock}>
          <CcfTypography className={styles.label}>
            {translate('new_group_name')}
          </CcfTypography>
          <CcfTextField
            fullWidth
            id="GroupName"
            variant="outlined"
            size="small"
            value={groupName}
            onChange={(e) => setgroupName(e.target.value)}
            focused={false}
            InputProps={{
              className: styles.inputTxt,
            }}
          />
        </CcfBox>
        <CcfBox className={styles.groupBlock}>
          <CcfTypography className={styles.label}>
            {translate('add_member')}
          </CcfTypography>
          <Autocomplete
            freeSolo
            options={filteredAgentList}
            getOptionLabel={(option) => option.name}
            inputValue={searchTxt}
            value={null} // Set value to null to prevent showing the selected value
            onInputChange={(event, newInputValue) => {
              setSearchTxt(newInputValue);
              searchAgentHandler({ target: { value: newInputValue } });
              if (!newInputValue) {
                setFilteredAgentList([]); // Clear the filtered list when input is cleared
              }
            }}
            onChange={(event, value) => {
              if (value) {
                addAgentHandler(value);
              }
            }}
            renderOption={(props, option, index) => (
              <li {...props} className={styles.customOptionContainer}>
                <CcfBox className={styles.optionBlock}>
                  <CcfAvatar
                    width={22}
                    height={22}
                    fontSize={12}
                    bgcolor="#009DE0"
                    borderColor="#009DE0"
                    border={true}
                  >
                    {option.name
                      .split(' ')
                      .map((n: string) => n[0])
                      .join('')}
                  </CcfAvatar>
                  <CcfTypography className={styles.agentNameTxt}>
                    {option.name}
                  </CcfTypography>
                </CcfBox>
                <CcfBox className={styles.optionBlock}>
                  {renderStatusIcon(option.status)}
                  <CcfTypography className={styles.agentStatusTxt}>
                    {option.status}
                  </CcfTypography>
                </CcfBox>
              </li>
            )}
            renderInput={(params) => (
              <CcfTextField
                {...params}
                size="small"
                style={{
                  background: 'white',
                }}
                className={styles.textField}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <CcfSearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: null,
                }}
                placeholder="Search"
                variant="outlined"
                fullWidth
              />
            )}
          />
        </CcfBox>
        <CcfBox className={styles.groupMembersContainer}>
          {groupMembers.length > 0 &&
            groupMembers.map((member: any, index: number) => (
              <CcfBox key={member.name} className={styles.groupMemberBlock}>
                <CcfTypography className={styles.memberName}>
                  <CcfAvatar
                    width={25}
                    height={25}
                    fontSize={12}
                    bgcolor={avatarColors[index % avatarColors.length]}
                    borderColor={avatarColors[index % avatarColors.length]}
                    border={true}
                  >
                    {member.name
                      .split(' ')
                      .map((n: string) => n[0])
                      .join('')}
                  </CcfAvatar>
                  {member.name}
                </CcfTypography>
                <CcfCloseIcon
                  cursor={'pointer'}
                  onClick={() => removeMemberHandler(member)}
                  fontSize="small"
                />
              </CcfBox>
            ))}
        </CcfBox>
        <CcfBox className={styles.BtnContainer}>
          <CcfButton className={`${styles.btn} ${styles.CancelButton}`}>
            <CcfTypography
              className={`${styles.btnText} ${styles.txtCancel}`}
              onClick={handleClose}
            >
              {translate('cancel')}
            </CcfTypography>
          </CcfButton>
          <CcfButton primary className={`${styles.btn} ${styles.createButton}`}>
            <CcfTypography
              className={`${styles.btnText} ${styles.txtCreate}`}
              onClick={createGroupHandler}
            >
              {translate('create')}
            </CcfTypography>
          </CcfButton>
        </CcfBox>
      </CcfBox>
    </Popover>
  );
}

export default ConversationCreateGroupPopover;
